/**
 * Top-bar banner that surfaces rate-limit status across all customer pages.
 *
 * Three visibility states:
 *   - Hidden: every tracked endpoint is below 80%.
 *   - Yellow warning: at least one endpoint is in `approaching_limit`
 *     (>=80% used). Banner stays collapsed showing a one-line summary;
 *     clicking expands a per-endpoint list.
 *   - Red blocking: at least one endpoint is `at_limit` (>=100% used).
 *     Banner is sticky and shows reset countdown.
 *
 * Side-effect: when an endpoint first crosses 80%, fires a console.warn
 * marker AND a one-time-per-day toast (stored in localStorage keyed by
 * `dca:warn:{endpoint}:{date}` so it doesn't re-trigger on every page nav).
 *
 * Hidden when the user isn't authenticated or when the endpoint returns
 * 401 / null — the hook returns `null` in those cases and we no-op.
 */

import { useEffect, useState } from 'react'
import { AlertTriangle, ShieldAlert, X, ChevronDown, ChevronUp } from 'lucide-react'
import { useRateLimitUsage, formatResetCountdown, type RateLimitEndpointUsage } from '@/hooks/use-rate-limits'

const DISMISS_KEY_PREFIX = 'dca:rl-banner-dismissed'
const WARN_TOAST_KEY_PREFIX = 'dca:rl-warn-shown'

function todayKey(): string {
  const d = new Date()
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
}

export function RateLimitBanner() {
  const { data: usage } = useRateLimitUsage()
  const [expanded, setExpanded] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  // Reset dismissal each new UTC day so the banner reappears tomorrow if
  // the user is still over a limit. We read once per mount; the explicit
  // dismiss state lives only in component memory + localStorage.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const key = `${DISMISS_KEY_PREFIX}:${todayKey()}`
    if (window.localStorage.getItem(key) === '1') setDismissed(true)
  }, [])

  // Fire a one-time warning toast (just a console.warn for now — wire to
  // your actual toast system later if you have one) when each endpoint
  // first crosses 80% today. Tracked per-endpoint-per-day in localStorage.
  useEffect(() => {
    if (!usage || typeof window === 'undefined') return
    for (const e of usage.endpoints) {
      if (e.state !== 'approaching_limit' && e.state !== 'at_limit') continue
      const key = `${WARN_TOAST_KEY_PREFIX}:${e.key}:${todayKey()}`
      if (window.localStorage.getItem(key) === '1') continue
      window.localStorage.setItem(key, '1')
      const stateLabel = e.state === 'at_limit' ? 'AT LIMIT' : 'approaching limit'
      console.warn(
        `[rate-limit] ${e.label}: ${e.used}/${e.cap} (${e.percent}%) — ${stateLabel}`,
      )
    }
  }, [usage])

  if (!usage || dismissed) return null
  if (!usage.any_at_limit && !usage.any_approaching) return null

  const blocked = usage.endpoints.filter((e) => e.state === 'at_limit')
  const approaching = usage.endpoints.filter((e) => e.state === 'approaching_limit')
  const isBlocking = blocked.length > 0

  function handleDismiss(): void {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(`${DISMISS_KEY_PREFIX}:${todayKey()}`, '1')
    setDismissed(true)
  }

  return (
    <div
      className={
        isBlocking
          ? 'border-b border-red-500/30 bg-red-500/10 text-red-200'
          : 'border-b border-amber-500/30 bg-amber-500/10 text-amber-200'
      }
      role="status"
      aria-live="polite"
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-2 sm:flex-row sm:items-center sm:gap-3 sm:px-6">
        {isBlocking ? (
          <ShieldAlert className="h-4 w-4 shrink-0" />
        ) : (
          <AlertTriangle className="h-4 w-4 shrink-0" />
        )}

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">
            {isBlocking ? (
              <>
                Daily limit reached on{' '}
                <span className="font-semibold">
                  {blocked.map((e) => e.label).join(', ')}
                </span>
                . Resets in {formatResetCountdown(usage.resets_at)} (midnight UTC).
              </>
            ) : (
              <>
                Approaching daily limit on{' '}
                <span className="font-semibold">
                  {approaching.map((e) => e.label).join(', ')}
                </span>
                .{' '}
                <span className="opacity-80">
                  {approaching.length === 1
                    ? `${approaching[0]!.remaining ?? 0} left today.`
                    : `Check usage for details.`}
                </span>
              </>
            )}
          </p>
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium hover:bg-white/5"
            aria-expanded={expanded}
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {expanded ? 'Hide details' : 'Details'}
          </button>
          {!isBlocking && (
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Dismiss until tomorrow"
              className="inline-flex items-center rounded px-2 py-1 text-xs font-medium hover:bg-white/5"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="mx-auto max-w-7xl border-t border-current/20 px-4 py-2 sm:px-6">
          <ul className="grid gap-1.5 text-xs sm:grid-cols-2 lg:grid-cols-3">
            {usage.endpoints
              .filter((e) => e.state === 'at_limit' || e.state === 'approaching_limit')
              .map((e) => (
                <UsageRow key={e.key} endpoint={e} />
              ))}
          </ul>
          <p className="mt-2 text-[11px] opacity-70">
            Limits reset at midnight UTC ({formatResetCountdown(usage.resets_at)} from now).
            Upgrade your plan for higher daily caps.
          </p>
        </div>
      )}
    </div>
  )
}

function UsageRow({ endpoint }: { readonly endpoint: RateLimitEndpointUsage }) {
  const barColor =
    endpoint.state === 'at_limit'
      ? 'bg-red-400'
      : endpoint.state === 'approaching_limit'
        ? 'bg-amber-400'
        : 'bg-emerald-400'
  return (
    <li className="flex items-center gap-2 rounded-md bg-black/15 px-2 py-1.5">
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-xs font-medium">{endpoint.label}</span>
          <span className="shrink-0 font-mono text-[10px] tabular-nums opacity-80">
            {endpoint.used}/{endpoint.cap ?? '–'}
          </span>
        </div>
        <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-white/10">
          <div
            className={`h-full ${barColor}`}
            style={{ width: `${Math.min(100, endpoint.percent)}%` }}
          />
        </div>
      </div>
    </li>
  )
}
